import React from 'react'
import {
  BUTTON_ACTIONS,
  ButtonConfig,
  createDefaultPopupActionButtons,
  createDefaultPopupFields,
  isPopupButtonAction,
  POPUP_INNER_ACTIONS,
  PopupActionButton,
  PopupInnerAction
} from './buttonFieldUtils'

interface Props {
  config: ButtonConfig
  onFieldChange: (field: keyof ButtonConfig, value: string | boolean | PopupActionButton[]) => void
}

const PopupButtonFieldsEditor: React.FC<Props> = ({ config, onFieldChange }) => {
  const action = config.buttonAction
  if (!isPopupButtonAction(action)) return null

  const popupButtons = config.popupButtons || []

  const updatePopupButton = (
    buttonIndex: number,
    field: keyof PopupActionButton,
    value: string
  ) => {
    const nextButtons = [...popupButtons]
    nextButtons[buttonIndex] = { ...nextButtons[buttonIndex], [field]: value }
    onFieldChange('popupButtons', nextButtons)
  }

  return (
    <div className='border rounded p-3 mb-3 bg-white'>
      <h6 className='mb-3'>Popup settings</h6>
      <div className='mb-2'>
        <label className='form-label'>Popup title</label>
        <input
          type='text'
          className='form-control form-control-sm'
          value={config.popupTitle || ''}
          onChange={(e) => onFieldChange('popupTitle', e.target.value)}
        />
      </div>
      <div className='mb-2'>
        <label className='form-label'>Popup message</label>
        <textarea
          className='form-control form-control-sm'
          rows={3}
          value={config.popupMessage || ''}
          onChange={(e) => onFieldChange('popupMessage', e.target.value)}
        />
      </div>

      {action === 'popup' ? (
        <div className='mb-2'>
          <label className='form-label'>Close button text</label>
          <input
            type='text'
            className='form-control form-control-sm'
            value={config.popupCloseText || 'Close'}
            onChange={(e) => onFieldChange('popupCloseText', e.target.value)}
          />
        </div>
      ) : (
        <>
          {popupButtons.map((button, buttonIndex) => (
            <div
              key={button.id || `popup-inner-${buttonIndex}`}
              className='border rounded p-2 mb-2'
            >
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <strong>Popup button {buttonIndex + 1}</strong>
                <button
                  type='button'
                  className='btn btn-sm btn-outline-danger'
                  onClick={() => {
                    onFieldChange(
                      'popupButtons',
                      popupButtons.filter((_, index) => index !== buttonIndex)
                    )
                  }}
                >
                  Remove
                </button>
              </div>
              <div className='mb-2'>
                <label className='form-label'>Button text</label>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  value={button.text}
                  onChange={(e) => updatePopupButton(buttonIndex, 'text', e.target.value)}
                />
              </div>
              <div className='mb-2'>
                <label className='form-label'>Action on click</label>
                <select
                  className='form-select form-select-sm'
                  value={button.action}
                  onChange={(e) =>
                    updatePopupButton(buttonIndex, 'action', e.target.value as PopupInnerAction)
                  }
                >
                  {POPUP_INNER_ACTIONS.map((innerAction) => (
                    <option key={innerAction.value} value={innerAction.value}>
                      {innerAction.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='row g-2'>
                <div className='col-6'>
                  <label className='form-label'>Text color</label>
                  <input
                    type='color'
                    className='form-control form-control-color form-control-sm'
                    value={button.btnTxtColor}
                    onChange={(e) => updatePopupButton(buttonIndex, 'btnTxtColor', e.target.value)}
                  />
                </div>
                <div className='col-6'>
                  <label className='form-label'>Button color</label>
                  <input
                    type='color'
                    className='form-control form-control-color form-control-sm'
                    value={button.btnBgColor}
                    onChange={(e) => updatePopupButton(buttonIndex, 'btnBgColor', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type='button'
            className='btn btn-sm btn-outline-primary'
            onClick={() =>
              onFieldChange('popupButtons', [
                ...popupButtons,
                {
                  ...createDefaultPopupActionButtons()[0],
                  id: `popup-btn-${Date.now()}`,
                  text: 'OK'
                }
              ])
            }
          >
            Add popup button
          </button>
        </>
      )}
    </div>
  )
}

export { BUTTON_ACTIONS, createDefaultPopupFields, isPopupButtonAction }
export default PopupButtonFieldsEditor
